import { addDumpedLanguageFallback, addReplacementLocale } from '../components/Contexts/InputOutputHandler'
import { DEFAULT_LANGUAGE_DATA } from '../models/constants'
import type { AvailableLanguages } from '../models/types'
import { allowedLocalesForMicrosoftWithVisemes, allowedLocalesForMicrosoftWithoutVisemes } from '../translations/allowedLocales'

interface SiteCulture {
  culture: string
  default: boolean
}

const fetchAvailableLanguages = async (baseAddress: string, needVisemes?: boolean): Promise<AvailableLanguages> => {
  const fullAddress = `${baseAddress}api/graphql?query={siteCultures{culture,default}}`
  const languages: AvailableLanguages = await fetch(fullAddress)
    .then((res) => res.json())
    .then((json) => {
      if (!json.data?.siteCultures?.length) {
        return DEFAULT_LANGUAGE_DATA
      }

      const { processedDefaultLanguage, processedSupported } = processLanguageData(
        json.data.siteCultures.find((siteCulture) => siteCulture.default)?.culture || json.data.siteCultures[0].culture,
        (json.data.siteCultures as Array<SiteCulture>).map((siteCulture) => siteCulture.culture),
        needVisemes
      )

      return {
        all: processedSupported,
        default: processedDefaultLanguage
      }
    })
    .catch((error) => {
      console.log(error)
      return DEFAULT_LANGUAGE_DATA
    })

  return languages
}

const processLanguageData = (defaultLanguage: string, supported: Array<string>, needVisemes?: boolean): { processedDefaultLanguage: string; processedSupported: Array<string> } => {
  const allowedLocales = needVisemes ? allowedLocalesForMicrosoftWithVisemes : allowedLocalesForMicrosoftWithoutVisemes
  let processedDefaultLanguage = allowedLocales.includes(defaultLanguage) ? defaultLanguage : 'fr-FR'
  const processedSupported: Array<string> = []

  supported.forEach((culture) => {
    // Fallback mechanism if the language code is not supported by Microsoft cognitive services
    if (!allowedLocales.includes(culture)) {
      const l = culture.split('-')[0]
      const otherLocalesFromSameLanguage = supported.filter((localeCode) => localeCode.startsWith(`${l}-`)).find((locale) => allowedLocales.includes(locale))

      // If we do find another locale code that contains the same language and is available for speech services, we dump the one that we are testing
      // Otherwise, we add the first available in the list of allowed ones if there is one of the same language.
      if (otherLocalesFromSameLanguage) {
        // If the dumped language is the default one, change it with the fallback one
        culture === defaultLanguage && (processedDefaultLanguage = otherLocalesFromSameLanguage)
        addReplacementLocale(culture, otherLocalesFromSameLanguage)
        console.log(`Retorik Framework > invalid locale "${culture}" was dumped because an existing locale for this language was found ("${otherLocalesFromSameLanguage}").`)
      } else {
        const fallback = allowedLocales.find((locale) => locale.startsWith(`${l}-`))
        if (fallback) {
          processedSupported.push(fallback)
          // If the dumped language is the default one, change it with the fallback one
          culture === defaultLanguage && (processedDefaultLanguage = fallback)
          // We keep a relation between the dumped one and the replacement one in "dumpedLanguageFallback" to re-inject the dumped one in the activities so that Retorik Studio
          // gets the same one as the one we fetched and not the replacement one
          addDumpedLanguageFallback(fallback, culture)
          addReplacementLocale(culture, fallback)
          console.log(`Retorik Framework > invalid locale "${culture}" was replaced by "${fallback}".`)
        } else {
          console.log(`Retorik Framework > invalid locale "${culture}" was dumped and no replacement was found for this language.`)
        }
      }
    } else {
      processedSupported.push(culture)
    }
  })

  return { processedDefaultLanguage, processedSupported }
}

export default fetchAvailableLanguages
export { processLanguageData }
